import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { faCog, faTimes } from '@fortawesome/free-solid-svg-icons';

const PARAMS_TRANSLATION_MAP: Record<string, string> = {
  language: 'CONFIGURATIONS.FORM.LANGUAGE',
  layoutAnalysis: 'CONFIGURATIONS.FORM.LAYOUT_ANALYSIS',
  inputType: 'CONFIGURATIONS.FORM.INPUT_TYPE',
  ocrType: 'CONFIGURATIONS.FORM.OCR_TYPE',
  outputType: 'CONFIGURATIONS.FORM.OUTPUT_TYPE',
  textType: 'CONFIGURATIONS.FORM.TEXT_TYPE',
  ocrCrop: 'CONFIGURATIONS.FORM.OCR_CROP',
  autoCut: 'CONFIGURATIONS.FORM.AUTO_CUT',
  enhanceDocument: 'CONFIGURATIONS.FORM.ENHANCE_DOCUMENT',
  sortOcr: 'CONFIGURATIONS.FORM.SORT_OCR',
  adjustRotation: 'CONFIGURATIONS.FORM.ADJUST_ROTATION',
  splitPdf: 'CONFIGURATIONS.FORM.SPLIT_PDF',
  llm_model: 'CONFIGURATIONS.FORM.LLM_MODEL',
  url: 'CONFIGURATIONS.FORM.URL',
  retriever: 'CONFIGURATIONS.FORM.RETRIEVER_STRATEGY',
};

const TYPE_TRANSLATION_MAP: Record<string, string> = {
  delfos_server: 'SETTINGS.PREFERENCES.DELFOS_SERVER',
  delfos_llm: 'SETTINGS.PREFERENCES.DELFOS_LLM',
  di2winocr_server: 'SETTINGS.PREFERENCES.OCR_SERVER',
  di2winocr_features: 'SETTINGS.PREFERENCES.OCR_FEATURES',
  retriever: 'SETTINGS.PREFERENCES.RETRIEVER',
};

const ENGINE_VALUE_MAP: Record<string, string> = {
  azure: 'CONFIGURATIONS.FORM.OPTIONS.ENGINE.AZURE',
  azure4: 'CONFIGURATIONS.FORM.OPTIONS.ENGINE.AZURE4',
  google: 'CONFIGURATIONS.FORM.OPTIONS.ENGINE.GOOGLE',
  paddle: 'CONFIGURATIONS.FORM.OPTIONS.ENGINE.PADDLE',
  docling: 'CONFIGURATIONS.FORM.OPTIONS.ENGINE.DOCLING',
  raw_google: 'CONFIGURATIONS.FORM.OPTIONS.ENGINE.RAW_GOOGLE',
};

const VALUE_TRANSLATION_MAP: Record<string, Record<string, string>> = {
  inputType: {
    path: 'CONFIGURATIONS.FORM.OPTIONS.INPUT_TYPE.PATH',
    base64: 'CONFIGURATIONS.FORM.OPTIONS.INPUT_TYPE.BASE64',
  },
  ocrType: ENGINE_VALUE_MAP,
  outputType: ENGINE_VALUE_MAP,
  textType: {
    print: 'CONFIGURATIONS.FORM.OPTIONS.TEXT_TYPE.PRINT',
    hand: 'CONFIGURATIONS.FORM.OPTIONS.TEXT_TYPE.HAND',
    mix: 'CONFIGURATIONS.FORM.OPTIONS.TEXT_TYPE.MIX',
  },
  layoutAnalysis: {
    '': 'CONFIGURATIONS.FORM.OPTIONS.LAYOUT_ANALYSIS.NONE',
    'false': 'CONFIGURATIONS.FORM.OPTIONS.LAYOUT_ANALYSIS.NONE',
    'true': 'CONFIGURATIONS.FORM.OPTIONS.LAYOUT_ANALYSIS.AZURE_DOCUMENT',
    '1': 'CONFIGURATIONS.FORM.OPTIONS.LAYOUT_ANALYSIS.TABLE_TRANSFORMER',
    '2': 'CONFIGURATIONS.FORM.OPTIONS.LAYOUT_ANALYSIS.TABLE_STRUCTURE',
    '3': 'CONFIGURATIONS.FORM.OPTIONS.LAYOUT_ANALYSIS.OMR_V31',
    '4': 'CONFIGURATIONS.FORM.OPTIONS.LAYOUT_ANALYSIS.OMR_V32',
  },
  adjustRotation: {
    '': 'CONFIGURATIONS.FORM.OPTIONS.ADJUST_ROTATION.NONE',
    '1': 'CONFIGURATIONS.FORM.OPTIONS.ADJUST_ROTATION.PADDLE',
    '2': 'CONFIGURATIONS.FORM.OPTIONS.ADJUST_ROTATION.REDO_OCR',
  },
};

const BOOLEAN_KEYS = new Set(['ocrCrop', 'autoCut', 'enhanceDocument', 'sortOcr', 'splitPdf']);

@Component({
  selector: 'app-modal-details',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, TranslateModule],
  templateUrl: './modal-details.component.html',
  styleUrls: ['./modal-details.component.scss']
})
export class ModalDetailsComponent {
  @Input() show = false;
  @Input() configuration: any = null;
  @Output() close = new EventEmitter<void>();

  faCog = faCog;
  faTimes = faTimes;

  onClose() {
    this.close.emit();
  }

  get displayName(): string {
    return this.configuration?.name || this.configuration?.key || '';
  }

  get valueKeys(): string[] {
    return this.configuration?.value ? Object.keys(this.configuration.value) : [];
  }

  labelKey(key: string): string {
    return PARAMS_TRANSLATION_MAP[key] || key;
  }

  typeLabelKey(type: string): string {
    return TYPE_TRANSLATION_MAP[type] || type || '';
  }

  valueLabelKey(key: string, value: any): string {
    if (BOOLEAN_KEYS.has(key)) {
      return value ? 'COMMON.YES' : 'COMMON.NO';
    }
    const normalized = value === null || value === undefined ? '' : String(value);
    const map = VALUE_TRANSLATION_MAP[key];
    if (map && Object.prototype.hasOwnProperty.call(map, normalized)) {
      return map[normalized];
    }
    if (typeof value === 'boolean') {
      return value ? 'COMMON.YES' : 'COMMON.NO';
    }
    return normalized;
  }
}
